import { Directionality } from '@angular/cdk/bidi';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { ElementRef, Inject, Injectable, Injector, NgZone, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subject, Subscription, switchMap, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { ObservableContentService } from '../../services';
import { PlayerAndPersonaMpxService } from '../../services/mpx';
import { Screen, ScreenService } from '../../services/screen.service';
import { remove } from '../../utils';
import { PreviewOverlayComponent } from './preview-overlay.component';
import {
  ClosableOverlay,
  ClosableOverlayToken,
  PreviewOverlaySource,
  PreviewOverlayViewModelToken,
  ShallowPreviewOverlayViewModel,
} from './preview-overlay.view-model';

const GRID_GUTTERS = {
  [Screen.XS]: 8,
  [Screen.SM]: 8,
  [Screen.MD]: 8,
  [Screen.LG]: 16,
  [Screen.XL]: 16,
};

const GRID_CONTAINER_PADDINGS = {
  [Screen.XS]: 16,
  [Screen.SM]: 31,
  [Screen.MD]: 42,
  [Screen.LG]: 55,
  [Screen.XL]: 77,
};

const TOTAL_OVERLAY_DELAY_MS = 750;
const SCROLL_DEBOUNCE_MS = 300;

@Injectable({
  providedIn: 'root',
})
export class PreviewOverlayService {
  public overlayRef?: OverlayRef;
  private subscription: Subscription;
  private overlayWidth: number;
  private readonly windowResized$ = new Subject<void>();
  private readonly overlaySources: PreviewOverlaySource[] = [];
  private currentOverlaySource: PreviewOverlaySource;
  private currentSubscriptionCompleted = false;

  public readonly _windowScrolled$ = new Subject<void>();
  public readonly windowScrolled$ = this._windowScrolled$.pipe(startWith(undefined), debounceTime(SCROLL_DEBOUNCE_MS));

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId,
    private readonly overlay: Overlay,
    private readonly directionality: Directionality,
    private readonly injector: Injector,
    private readonly screenService: ScreenService,
    private readonly zone: NgZone,
    private readonly mpxService: PlayerAndPersonaMpxService,
    private readonly observableContentService: ObservableContentService
  ) {
    this.listenToWindowResizeAndScroll();
    this.calculateOverlayWidth();
  }

  private listenToWindowResizeAndScroll() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      if (!this.document.defaultView) {
        return;
      }
      fromEvent(this.document.defaultView, 'resize')
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          this.zone.run(() => {
            this.windowResized$.next();
          });
        });

      fromEvent(this.document.defaultView, 'scroll').subscribe(() => {
        this.zone.run(() => this._windowScrolled$.next());
      });
    });
  }

  private calculateOverlayWidth() {
    combineLatest([this.windowResized$.pipe(startWith(true)), this.screenService.changed$]).subscribe(([_, screen]) => {
      const screenWidth = this.document.body.clientWidth;
      const containerPadding = GRID_CONTAINER_PADDINGS[screen];
      const gutterWidth = GRID_GUTTERS[screen];

      const cols = 24;
      const previewOverlayCols = 7;

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const containerWidthWithoutPaddings = screenWidth - containerPadding * 2;
      const guttersWidth = cols * gutterWidth;
      const colWidth = Math.ceil((containerWidthWithoutPaddings - guttersWidth) / cols);
      this.overlayWidth = previewOverlayCols * colWidth + (previewOverlayCols - 1) * gutterWidth;

      // Making sure the overlay is not thicker then the underlying element.
      // For some math rounding reasons the calculated width can be 1-2px smaller then the css grid sizes.
      const correction = 2;
      this.overlayWidth += correction;
    });
  }

  public createOverlay$(element: ElementRef, data: ShallowPreviewOverlayViewModel, source: PreviewOverlaySource): Observable<boolean> {
    const previewHoverState$ = new BehaviorSubject<boolean>(true);
    this.closeAndNotifyOverlaySources(source);
    this.currentOverlaySource = source;
    this.currentSubscriptionCompleted = false;

    this.subscription = new Subscription();

    const observableProgram = this.observableContentService.getObservableProgram$(data.content);

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    timer(TOTAL_OVERLAY_DELAY_MS / 3)
      .pipe(
        switchMap(() => combineLatest([observableProgram.coreContentData.get(), observableProgram.extendedContentData.get()]).pipe(take(1)))
      )
      // No subscription handling here, if a preview is being opened but the hover is terminated,
      // the content should still be fetched to cache it
      .subscribe();

    this.subscription.add(
      timer(TOTAL_OVERLAY_DELAY_MS).subscribe(() => {
        this.currentSubscriptionCompleted = true;
        this.overlayRef = this.overlay.create({
          direction: this.directionality,
          disposeOnNavigation: true,
          // TODO - get width and calculate from service
          width: this.overlayWidth,
          scrollStrategy: this.overlay.scrollStrategies.reposition({ autoClose: true, scrollThrottle: 0 }),
          positionStrategy: this.overlay
            .position()
            .flexibleConnectedTo(element)
            .withLockedPosition(true)
            .withFlexibleDimensions(false)
            .withPush(false)
            .withPositions([
              {
                originX: 'center',
                originY: 'center',
                overlayX: 'center',
                overlayY: 'center',
              },
              {
                originX: 'start',
                originY: 'center',
                overlayX: 'start',
                overlayY: 'center',
              },
              {
                originX: 'end',
                originY: 'center',
                overlayX: 'end',
                overlayY: 'center',
              },
            ]),
        });
        const portal = new ComponentPortal(PreviewOverlayComponent);
        portal.injector = Injector.create({
          providers: [
            {
              provide: PreviewOverlayViewModelToken,
              useValue: data,
            },
            {
              provide: ClosableOverlayToken,
              useValue: {
                setPreviewHoverState: ({ isMouseOver }) => {
                  previewHoverState$.next(isMouseOver);
                },
                onOverlayDestroyed: () => {
                  previewHoverState$.next(false);
                  previewHoverState$.complete();
                },
              } as ClosableOverlay,
            },
          ],
          parent: this.injector,
          name: 'PreviewOverlayInjector',
        });
        this.overlayRef.attach(portal);
      })
    );

    return previewHoverState$.asObservable();
  }

  public registerOverlaySource(source: PreviewOverlaySource) {
    this.overlaySources.push(source);
  }

  public unregisterOverlaySource(source: PreviewOverlaySource) {
    remove(this.overlaySources, source);
  }

  public closeOverlayOfSource(source: PreviewOverlaySource) {
    if (this.currentOverlaySource !== source) {
      return;
    }

    this.closeAndNotifyOverlaySources(source);
    this.currentOverlaySource = undefined;
  }

  public closePendingOverlayOfSource(source: PreviewOverlaySource): boolean {
    if (this.currentOverlaySource !== source) {
      return false;
    }

    if (this.currentSubscriptionCompleted) {
      return false;
    }

    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.currentOverlaySource = undefined;

    return true;
  }

  public closeCurrentOverlay(): void {
    this.closeAndNotifyOverlaySources();
  }

  private closeAndNotifyOverlaySources(currentSource?: PreviewOverlaySource) {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.currentSubscriptionCompleted = false;

    for (const source of this.overlaySources) {
      if (source !== currentSource) {
        source.notifyOverlayClosed();
      }
    }
  }
}
